'use client';
import * as React from 'react';
import Link from 'next/link';
import { useLocale } from 'next-intl';
import { parseHtml } from '@/lib/utils/html-parser-adapter';
import useAuth from '@/hooks/useAuth';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { getStoreDataByKeyParam } from '@/clients/customer/data-stores';
import useSWR from 'swr';

const fetcher = async (): Promise<any> => {
  const response = await getStoreDataByKeyParam('web.content.store.profile');
  return response.data;
};

export default function CashbackInfo() {
  const locale = useLocale();
  const { isAnyUserLoggedIn } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);

  const { data, error, isLoading, mutate } = useSWR<any>(
    ['cashback-info'],
    () => fetcher(),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      // Keep data fresh for 60 seconds (matching server-side cache)
      dedupingInterval: 60000,
    }
  );

  if (isLoading) {
    return (
      <div className="mb-12 space-y-5 rounded bg-white px-5 py-4 shadow-lg lg:mb-0 lg:max-w-[324px] lg:py-10">
        <div className="space-y-4">
          {/* Title */}
          <div className="h-6 w-40 animate-pulse rounded bg-gray-200" />

          {/* Paragraph */}
          <div className="space-y-2">
            <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-4/6 animate-pulse rounded bg-gray-200" />
          </div>

          {/* Button */}
          <div className="h-11 w-full animate-pulse rounded-full bg-gray-200" />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return null;
  }

  const contents = data?.data?.[0]?.value;

  function getContent(path: string) {
    return path.split('.').reduce((obj, part) => {
      if (part === 'locale') {
        return obj[locale];
      }
      return obj[part];
    }, contents);
  }

  return (
    <div
      className={clsx(
        'mb-12 space-y-5 rounded bg-white px-5 py-4 shadow-lg lg:mb-0 lg:max-w-[324px] lg:py-10'
      )}
    >
      <div className="space-y-4">
        {/* Mobile/Tablet Accordion Header */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center justify-between text-left lg:pointer-events-none"
        >
          <h2 className="text-lg font-semibold">
            {getContent('howItWorks.title.locale')}:
          </h2>
          <span className="lg:hidden">
            {isOpen ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </span>
        </button>

        {/* Accordion Content - Hidden on mobile/tablet when closed, always visible on desktop */}
        <div className={`space-y-4 ${isOpen ? 'block' : 'hidden'} lg:block`}>
          <div>
            <ul className="list-inside space-y-3 text-sm">
              {getContent('howItWorks.steps.locale').map((step: string) => (
                <li key={step}>
                  {'>'} {parseHtml(step)}
                </li>
              ))}
            </ul>
          </div>

          {!isAnyUserLoggedIn && (
            <div>
              <Link
                href={getContent(`loginSection.title.locale.link`)}
                className="block w-full rounded-full bg-gray-900 py-3 text-center text-sm font-semibold text-white"
              >
                {getContent(`loginSection.title.locale.text`)}
              </Link>
            </div>
          )}

          <div className="space-y-2">
            <h3 className="font-semibold">
              {getContent('importantInfo.title.locale')}
            </h3>
            <p
              className="text-sm text-gray-600"
              dangerouslySetInnerHTML={{
                __html: getContent('importantInfo.content.locale'),
              }}
            />
            <p
              className="text-sm text-gray-600"
              dangerouslySetInnerHTML={{
                __html: getContent('importantInfo.content_2nd.locale'),
              }}
            />
          </div>

          <div className="space-y-2" suppressHydrationWarning>
            <p
              className="text-sm [&_a]:font-bold [&_a]:underline"
              dangerouslySetInnerHTML={{
                __html: getContent('importantInfo.additionalInfo.locale'),
              }}
            />
          </div>

          <div className="space-y-2 pt-4">
            <h3 className="font-semibold">
              {getContent('newsletter.title.locale')}
            </h3>
            <p
              className="text-sm [&_a]:font-bold"
              dangerouslySetInnerHTML={{
                __html: getContent('newsletter.description.locale'),
              }}
            />
            <Link
              href={getContent('newsletter.button.locale.link')}
              className="mt-2 block w-fit rounded-full bg-gray-900 px-6 py-2 text-center text-sm font-semibold text-white"
            >
              {getContent('newsletter.button.locale.text')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
